import { useRef, useState } from 'react'
import type { ChangeEvent, DragEvent } from 'react'
import { cn, Label, UploadIcon } from '@quickmd-llc/design-system'

interface MedicationImageUploadProps {
  value: File | null
  previewUrl: string | null
  onChange: (file: File) => void
  hasError?: boolean
}

export function MedicationImageUpload({ previewUrl, onChange, hasError }: MedicationImageUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [isDragging, setIsDragging] = useState(false)

  function handleFile(file: File) {
    if (file.type.startsWith('image/')) {
      onChange(file)
    }
  }

  function handleDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault()
    setIsDragging(false)
    const file = e.dataTransfer.files[0]
    if (file) handleFile(file)
  }

  function handleDragOver(e: DragEvent<HTMLDivElement>) {
    e.preventDefault()
    setIsDragging(true)
  }

  function handleDragLeave(e: DragEvent<HTMLDivElement>) {
    e.preventDefault()
    setIsDragging(false)
  }

  function handleInputChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (file) handleFile(file)
  }

  const hasImage = Boolean(previewUrl)

  return (
    <div className='flex flex-shrink-0 flex-col items-center gap-sp-1'>
      <div
        className={cn(
          'relative flex h-sp-20 w-sp-20 cursor-pointer flex-col items-center justify-center overflow-hidden rounded-larger border transition-colors',
          hasImage ? 'border-solid' : 'border-dashed',
          isDragging ? 'border-highlight bg-surface-highlight'
          : hasError ? 'border-border-error bg-surface-error hover:border-highlight hover:bg-surface-highlight'
          : 'border-border-dark bg-white hover:border-highlight hover:bg-surface-highlight',
        )}
        onClick={() => inputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        role='button'
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click()
        }}
        aria-label='Upload medication image'>
        {previewUrl ?
          <>
            <img
              src={previewUrl}
              alt='Medication preview'
              className={cn('h-full w-full object-cover transition-opacity', isDragging && 'opacity-40')}
            />
            {isDragging ?
              <div className='absolute inset-0 flex flex-col items-center justify-center'>
                <UploadIcon iconSize='s' className='text-highlight' />
                <Label size='md' className='mt-sp-0.5 text-highlight'>
                  Drop to replace
                </Label>
              </div>
            : null}
          </>
        : <div className='flex flex-col items-center gap-sp-0.5 p-sp-1 text-center'>
            {isDragging ?
              <>
                <UploadIcon iconSize='s' className='text-highlight' />
                <Label size='md' className='text-highlight'>
                  Drop image
                </Label>
              </>
            : <>
                <UploadIcon iconSize='s' className='text-text-dark' />
                <Label size='md'>Upload photo</Label>
              </>
            }
          </div>
        }
      </div>

      {hasImage ?
        <button
          type='button'
          onClick={() => inputRef.current?.click()}
          className='flex items-center gap-sp-1 px-sp-2 py-sp-0.75 text-text-dark hover:underline'>
          <UploadIcon iconSize='s' className='text-text-dark' />
          <Label size='md'>Change photo</Label>
        </button>
      : null}

      <input ref={inputRef} type='file' accept='image/*' className='hidden' onChange={handleInputChange} />
    </div>
  )
}
